import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type BankAccountOption = { id: number; label: string };

type ItemType = "MOVEMENT" | "TRANSFER" | "SKIP";

type ExistingMovement = { id: number; amount: number; occurredAt: string; type: "IN" | "OUT" };

type OfxItem = {
  fitId: string;
  date: string;
  amount: number;
  type: "IN" | "OUT";
  memo: string;
  status: "NEW" | "DUPLICATE";
  existingMovementId: number | null;
  itemType: ItemType;
  description: string;
  categoryId?: number | null;
  categoryLabel?: string | null;
  costCenterId?: number | null;
  costCenterLabel?: string | null;
  accountId?: number | null;
  accountLabel?: string | null;
  transferToBankAccountId?: number | null;
  transferPairFitId?: string | null;
  ruleId?: number | null;
  selected: boolean;
  createPayable: boolean;
  createReceivable: boolean;
  reconciled: boolean;
  shouldReconcile: boolean;
};

type PreviewResult = {
  bankAccount: {
    id: number;
    agency: string;
    accountNumber: string;
    bankName: string | null;
    remunerationAccountId: number | null;
    remunerationAccountLabel: string | null;
  };
  ofxAccountId: string | null;
  currency: string;
  dateStart: string | null;
  dateEnd: string | null;
  items: Omit<OfxItem, "selected" | "createPayable" | "createReceivable" | "reconciled" | "shouldReconcile">[];
  existingMovements: ExistingMovement[];
  stats: { total: number; new: number; duplicate: number; transfer: number };
};

type OfxRule = {
  id: number;
  bankAccountId: number | null;
  memoContains: string;
  txType: string | null;
  action: string;
  description: string | null;
  categoryId: number | null;
  costCenterId: number | null;
  accountId: number | null;
  transferToBankAccountId: number | null;
};

type Option = { id: number; label: string };

const ACTION_LABELS: Record<string, string> = {
  CATEGORIZE: "Categorizar",
  TRANSFER: "Transferência",
  SKIP: "Ignorar",
};

const startOfDayMs = (dateStr: string) => {
  const d = new Date(dateStr);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

export const OFXImportPage: React.FC = () => {
  const { client } = useAuth();

  // Step 1
  const [bankAccounts, setBankAccounts] = useState<BankAccountOption[]>([]);
  const [bankAccountId, setBankAccountId] = useState<number | "">("");
  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<"ofx" | "pdf-genial">("ofx");
  const [pdfPassword, setPdfPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // Step 2
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [items, setItems] = useState<OfxItem[]>([]);
  const [existingMovements, setExistingMovements] = useState<ExistingMovement[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<{ inserted: number } | null>(null);

  // Rules
  const [rules, setRules] = useState<OfxRule[]>([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [editingRule, setEditingRule] = useState<OfxRule | null>(null);
  const [editRuleValues, setEditRuleValues] = useState<Partial<OfxRule>>({});
  const [editRuleAccountLabel, setEditRuleAccountLabel] = useState<string | undefined>(undefined);
  const [savingEditRule, setSavingEditRule] = useState(false);

  // Combobox options for rules form
  const [allCategories, setAllCategories] = useState<{ id: number; label: string }[]>([]);
  const [allCostCenters, setAllCostCenters] = useState<{ id: number; label: string }[]>([]);

  // Duplicate movement modal
  const [duplicateModal, setDuplicateModal] = useState<{ id: number; data: Record<string, unknown> | null } | null>(null);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<number | null>(null);

  const openDuplicateModal = async (movementId: number) => {
    setDuplicateModal({ id: movementId, data: null });
    setLoadingDuplicate(true);
    try {
      const res = await client.get(`/modules/movements/${movementId}`);
      if (res.ok) {
        const payload = (await res.json()) as { data: Record<string, unknown> };
        setDuplicateModal({ id: movementId, data: payload.data });
      }
    } finally {
      setLoadingDuplicate(false);
    }
  };

  const reconcileMovement = async (movementId: number) => {
    setReconcilingId(movementId);
    try {
      const res = await client.post(`/modules/movements/${movementId}/conciliate`, {
        status: "MATCHED",
        notes: "Reconciliado via importação OFX",
      });
      if (res.ok) {
        // Mark the corresponding item as reconciled
        setItems((prev) =>
          prev.map((it) =>
            it.existingMovementId === movementId ? { ...it, reconciled: true } : it
          )
        );
        // Refresh modal data to show updated reconciliation status
        if (duplicateModal?.id === movementId) {
          const res2 = await client.get(`/modules/movements/${movementId}`);
          if (res2.ok) {
            const payload2 = (await res2.json()) as { data: Record<string, unknown> };
            setDuplicateModal({ id: movementId, data: payload2.data });
          }
        }
      }
    } finally {
      setReconcilingId(null);
    }
  };

  // Re-matching duplicates client-side
  const recalculateDuplicates = useCallback(
    (currentItems: OfxItem[], movements: ExistingMovement[], dismissed: Set<number>): OfxItem[] => {
      const claimedIds = new Set<number>();
      return currentItems.map((item) => {
        if (item.itemType === "TRANSFER" || item.itemType === "SKIP") return item;
        const txDay = startOfDayMs(item.date);
        const match = movements.find(
          (m) =>
            !claimedIds.has(m.id) &&
            !dismissed.has(m.id) &&
            m.type === item.type &&
            Math.abs(m.amount - item.amount) < 0.01 &&
            startOfDayMs(m.occurredAt) === txDay
        );
        if (match) claimedIds.add(match.id);
        const wasDuplicate = item.status === "DUPLICATE";
        const isNowDuplicate = !!match;
        return {
          ...item,
          status: isNowDuplicate ? "DUPLICATE" : "NEW",
          existingMovementId: match?.id ?? null,
          // If it was a duplicate and now has no match: auto-select + flag for reconciliation on commit
          selected: isNowDuplicate ? false : (wasDuplicate ? true : item.selected),
          shouldReconcile: isNowDuplicate ? item.shouldReconcile : (wasDuplicate ? true : item.shouldReconcile),
          reconciled: isNowDuplicate ? item.reconciled : false,
        };
      });
    },
    []
  );

  const dismissDuplicate = useCallback(
    (movementId: number) => {
      const newDismissed = new Set([...dismissedIds, movementId]);
      setDismissedIds(newDismissed);
      setItems((prev) => recalculateDuplicates(prev, existingMovements, newDismissed));
    },
    [dismissedIds, existingMovements, recalculateDuplicates]
  );

  // Bulk defaults
  const [defaultCategoryOption, setDefaultCategoryOption] = useState<Option | null>(null);
  const [defaultCostCenterOption, setDefaultCostCenterOption] = useState<Option | null>(null);
  const [defaultAccountOption, setDefaultAccountOption] = useState<Option | null>(null);

  useEffect(() => {
    client.get("/modules/bank-accounts?take=100&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; agency: string; accountNumber: string; bank?: { name: string } | null }>;
      };
      setBankAccounts(
        payload.data.map((b) => ({
          id: b.id,
          label: b.bank
            ? `${b.bank.name} — ag ${b.agency} / cc ${b.accountNumber}`
            : `ag ${b.agency} / cc ${b.accountNumber}`,
        }))
      );
    });
  }, [client]);

  // Load all categories and cost centers for comboboxes
  useEffect(() => {
    client.get("/modules/categories?take=200&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      setAllCategories(payload.data.map((c) => ({ id: c.id, label: c.code ? `${c.code} ${c.name}` : c.name })));
    });
    client.get("/modules/cost-centers?take=200&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      setAllCostCenters(payload.data.map((c) => ({ id: c.id, label: `${c.code ?? ""} ${c.name}`.trim() })));
    });
  }, [client]);

  const loadRules = useCallback(async (baId?: number | "") => {
    const id = baId ?? bankAccountId;
    if (!id) return;
    const res = await client.get(`/integrations/ofx/rules?bankAccountId=${id}`);
    if (!res.ok) return;
    const payload = (await res.json()) as { data: OfxRule[] };
    setRules(payload.data);
  }, [client, bankAccountId]);

  useEffect(() => {
    if (bankAccountId) void loadRules(bankAccountId);
  }, [bankAccountId, loadRules]);

  const fetchCategories = useCallback(
    async (q: string) => {
      const res = await client.get(`/modules/categories?q=${encodeURIComponent(q)}&take=10&skip=0`);
      if (!res.ok) return [];
      const payload = (await res.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      return payload.data.map((c) => ({ id: c.id, label: c.code ? `${c.code} ${c.name}` : c.name }));
    },
    [client]
  );

  const fetchCostCenters = useCallback(
    async (q: string) => {
      const res = await client.get(`/modules/cost-centers?q=${encodeURIComponent(q)}&take=10&skip=0`);
      if (!res.ok) return [];
      const payload = (await res.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      return payload.data.map((c) => ({ id: c.id, label: `${c.code ?? ""} ${c.name}`.trim() }));
    },
    [client]
  );

  const fetchAccounts = useCallback(
    async (q: string) => {
      const res = await client.get(`/modules/accounts?q=${encodeURIComponent(q)}&take=10&skip=0`);
      if (!res.ok) return [];
      const payload = (await res.json()) as { data: Array<{ id: number; code: string; name: string }> };
      return payload.data.map((a) => ({ id: a.id, label: `${a.code} - ${a.name}` }));
    },
    [client]
  );

  const handlePreview = async () => {
    if (!file || !bankAccountId) {
      setStepError("Selecione a conta bancária e o arquivo.");
      return;
    }
    setLoading(true);
    setStepError(null);
    setPreview(null);
    setItems([]);
    setExistingMovements([]);
    setDismissedIds(new Set());
    setCommitResult(null);
    try {
      const token = localStorage.getItem("mpf_admin_access_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bankAccountId", String(bankAccountId));
      if (fileType === "pdf-genial" && pdfPassword) {
        formData.append("password", pdfPassword);
      }
      const endpoint =
        fileType === "pdf-genial"
          ? `${import.meta.env.VITE_API_URL as string}/integrations/ofx/preview-pdf`
          : `${import.meta.env.VITE_API_URL as string}/integrations/ofx/preview`;
      const res = await fetch(endpoint, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(payload.error ?? "Falha ao processar arquivo.");
      }
      const data = (await res.json()) as PreviewResult;
      setPreview(data);
      setExistingMovements(data.existingMovements ?? []);
      setItems(
        data.items.map((item) => ({
          ...item,
          selected: item.status === "NEW" && item.itemType !== "SKIP",
          createPayable: false,
          createReceivable: false,
          reconciled: false,
          shouldReconcile: false,
        }))
      );
      void loadRules(bankAccountId);
    } catch (err) {
      setStepError(err instanceof Error ? err.message : "Erro ao processar arquivo.");
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (idx: number, patch: Partial<OfxItem>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const applyDefaults = () => {
    setItems((prev) =>
      prev.map((it) =>
        it.selected && it.itemType === "MOVEMENT"
          ? {
              ...it,
              categoryId: defaultCategoryOption?.id ?? it.categoryId,
              categoryLabel: defaultCategoryOption?.label ?? it.categoryLabel,
              costCenterId: defaultCostCenterOption?.id ?? it.costCenterId,
              costCenterLabel: defaultCostCenterOption?.label ?? it.costCenterLabel,
              accountId: defaultAccountOption?.id ?? it.accountId,
              accountLabel: defaultAccountOption?.label ?? it.accountLabel,
            }
          : it
      )
    );
  };

  const selectedItems = useMemo(() => items.filter((it) => it.selected), [items]);

  const toggleAll = () => {
    const allSelected = items.filter((it) => it.status === "NEW").every((it) => it.selected);
    setItems((prev) =>
      prev.map((it) =>
        it.status === "NEW" ? { ...it, selected: !allSelected } : it
      )
    );
  };

  const handleCommit = async () => {
    if (selectedItems.length === 0 || !bankAccountId) return;
    setCommitting(true);
    setStepError(null);
    try {
      const res = await client.post("/integrations/ofx/commit", {
        bankAccountId: Number(bankAccountId),
        items: selectedItems.map((it) => ({
          fitId: it.fitId,
          date: it.date.slice(0, 10),
          amount: it.amount,
          type: it.type,
          description: it.description,
          itemType: it.itemType,
          categoryId: it.categoryId ?? null,
          costCenterId: it.costCenterId ?? null,
          accountId: it.accountId ?? null,
          transferToBankAccountId: it.transferToBankAccountId ?? null,
          transferPairFitId: it.transferPairFitId ?? null,
          createPayable: it.createPayable,
          createReceivable: it.createReceivable,
          shouldReconcile: it.shouldReconcile ?? false,
        })),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(payload.error ?? "Falha ao importar.");
      }
      const result = (await res.json()) as { inserted: number };
      setCommitResult(result);
      setItems([]);
      setPreview(null);
      setFile(null);
    } catch (err) {
      setStepError(err instanceof Error ? err.message : "Erro ao importar.");
    } finally {
      setCommitting(false);
    }
  };

  const saveAsRule = async (item: OfxItem) => {
    const memoContains = item.memo.slice(0, 30);
    setSavingRule(true);
    try {
      await client.post("/integrations/ofx/rules", {
        bankAccountId: bankAccountId || null,
        memoContains,
        txType: item.type,
        action: item.itemType === "TRANSFER" ? "TRANSFER" : item.itemType === "SKIP" ? "SKIP" : "CATEGORIZE",
        description: item.description !== item.memo ? item.description : null,
        categoryId: item.categoryId ?? null,
        costCenterId: item.costCenterId ?? null,
        accountId: item.accountId ?? null,
        transferToBankAccountId: item.transferToBankAccountId ?? null,
      });
      await loadRules();
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (id: number) => {
    await client.delete(`/integrations/ofx/rules/${id}`);
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRule = async (id: number, patch: Partial<OfxRule>) => {
    setSavingEditRule(true);
    try {
      await client.put(`/integrations/ofx/rules/${id}`, {
        memoContains: patch.memoContains,
        txType: patch.txType ?? null,
        action: patch.action ?? "CATEGORIZE",
        description: patch.description ?? null,
        categoryId: patch.categoryId ?? null,
        costCenterId: patch.costCenterId ?? null,
        accountId: patch.accountId ?? null,
        transferToBankAccountId: patch.transferToBankAccountId ?? null,
      });
      await loadRules();
      setEditingRule(null);
    } finally {
      setSavingEditRule(false);
    }
  };

  const itemTypeBadge = (t: ItemType) => {
    if (t === "TRANSFER") return <span className="badge text-bg-info text-dark">Transferência</span>;
    if (t === "SKIP") return <span className="badge text-bg-secondary">Ignorar</span>;
    return null;
  };

  return (
    <div className="container py-4" style={{ maxWidth: 1200 }}>
      <h1 className="mb-4">Importar OFX</h1>

      {commitResult && (
        <div className="alert alert-success">
          <strong>{commitResult.inserted}</strong> movimentação(ões) importada(s) com sucesso.
          <button className="btn btn-sm btn-outline-success ms-3" onClick={() => setCommitResult(null)}>
            Nova importação
          </button>
        </div>
      )}

      {!commitResult && (
        <>
          {/* Step 1 */}
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title mb-3">1. Selecionar arquivo e conta</h5>
              <div className="row g-3 align-items-end">
                <div className="col-md-5">
                  <label className="form-label">Conta bancária <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={bankAccountId}
                    onChange={(e) => {
                      setBankAccountId(e.target.value === "" ? "" : Number(e.target.value));
                      setPreview(null);
                      setItems([]);
                    }}
                  >
                    <option value="">Selecionar conta...</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>{b.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tipo de arquivo</label>
                  <select
                    className="form-select"
                    value={fileType}
                    onChange={(e) => {
                      setFileType(e.target.value as "ofx" | "pdf-genial");
                      setFile(null);
                      setPreview(null);
                      setItems([]);
                      setCommitResult(null);
                    }}
                  >
                    <option value="ofx">OFX</option>
                    <option value="pdf-genial">PDF Genial</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    {fileType === "pdf-genial" ? "Extrato PDF" : "Arquivo OFX"}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept={
                      fileType === "pdf-genial"
                        ? ".pdf,application/pdf"
                        : ".ofx,application/x-ofx,application/ofx"
                    }
                    onChange={(e) => {
                      setFile(e.target.files?.[0] ?? null);
                      setPreview(null);
                      setItems([]);
                      setCommitResult(null);
                    }}
                  />
                </div>
                {fileType === "pdf-genial" && (
                  <div className="col-md-2">
                    <label className="form-label">Senha do PDF</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="opcional"
                      value={pdfPassword}
                      onChange={(e) => setPdfPassword(e.target.value)}
                    />
                  </div>
                )}
                <div className="col-md-2">
                  <label className="form-label">&nbsp;</label>
                  <button
                    className="btn btn-primary w-100 d-block"
                    onClick={handlePreview}
                    disabled={loading || !file || !bankAccountId}
                  >
                    {loading ? "Analisando..." : "Analisar"}
                  </button>
                </div>
              </div>
              {stepError && <div className="alert alert-danger mt-3 py-2 mb-0">{stepError}</div>}
            </div>
          </div>

          {/* Rules panel */}
          {bankAccountId && (
            <div className="card shadow-sm mb-3">
              <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setRulesOpen((v) => !v)}
              >
                <span className="fw-semibold">
                  Regras de categorização automática
                  {rules.length > 0 && (
                    <span className="badge text-bg-primary ms-2">{rules.length}</span>
                  )}
                </span>
                <span>{rulesOpen ? "▲" : "▼"}</span>
              </div>
              {rulesOpen && (
                <div className="card-body p-0">
                  {rules.length === 0 ? (
                    <div className="p-3 text-muted small">
                      Nenhuma regra. Ao revisar transações, use "Salvar como regra" para criar padrões automáticos.
                    </div>
                  ) : (
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Memo contém</th>
                          <th>Tipo</th>
                          <th>Ação</th>
                          <th>Descrição</th>
                          <th>Conta</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((rule) => (
                          <React.Fragment key={rule.id}>
                            <tr>
                              <td><code className="small">{rule.memoContains}</code></td>
                              <td>
                                {rule.txType ? (
                                  <span className={`badge ${rule.txType === "IN" ? "text-bg-success" : "text-bg-danger"}`}>
                                    {rule.txType === "IN" ? "Entrada" : "Saída"}
                                  </span>
                                ) : <span className="text-muted">Ambos</span>}
                              </td>
                              <td>
                                <span className="badge text-bg-secondary">
                                  {ACTION_LABELS[rule.action] ?? rule.action}
                                </span>
                              </td>
                              <td className="text-muted small">{rule.description ?? "—"}</td>
                              <td>
                                <span className="text-muted small">
                                  {rule.bankAccountId ? "Esta conta" : "Global"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() => {
                                    setEditingRule(rule);
                                    setEditRuleValues({ ...rule });
                                    setEditRuleAccountLabel(undefined);
                                  }}
                                >
                                  Editar
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => void deleteRule(rule.id)}
                                >
                                  Excluir
                                </button>
                              </td>
                            </tr>
                            {editingRule?.id === rule.id && (
                              <tr>
                                <td colSpan={6}>
                                  <div className="p-3 border rounded bg-light">
                                    <div className="row g-2">
                                      <div className="col-md-4">
                                        <label className="form-label small mb-1">Memo contém</label>
                                        <input
                                          className="form-control form-control-sm"
                                          value={editRuleValues.memoContains ?? ""}
                                          onChange={(e) => setEditRuleValues((v) => ({ ...v, memoContains: e.target.value }))}
                                        />
                                      </div>
                                      <div className="col-md-2">
                                        <label className="form-label small mb-1">Tipo</label>
                                        <select
                                          className="form-select form-select-sm"
                                          value={editRuleValues.txType ?? ""}
                                          onChange={(e) => setEditRuleValues((v) => ({ ...v, txType: e.target.value || null }))}
                                        >
                                          <option value="">Ambos</option>
                                          <option value="IN">Entrada</option>
                                          <option value="OUT">Saída</option>
                                        </select>
                                      </div>
                                      <div className="col-md-2">
                                        <label className="form-label small mb-1">Ação</label>
                                        <select
                                          className="form-select form-select-sm"
                                          value={editRuleValues.action ?? "CATEGORIZE"}
                                          onChange={(e) => setEditRuleValues((v) => ({ ...v, action: e.target.value }))}
                                        >
                                          <option value="CATEGORIZE">Categorizar</option>
                                          <option value="TRANSFER">Transferência</option>
                                          <option value="SKIP">Ignorar</option>
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small mb-1">Descrição</label>
                                        <input
                                          className="form-control form-control-sm"
                                          value={editRuleValues.description ?? ""}
                                          onChange={(e) => setEditRuleValues((v) => ({ ...v, description: e.target.value || null }))}
                                        />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small mb-1">Categoria</label>
                                        <select
                                          className="form-select form-select-sm"
                                          value={editRuleValues.categoryId ?? ""}
                                          onChange={(e) => setEditRuleValues((v) => ({ ...v, categoryId: e.target.value ? Number(e.target.value) : null }))}
                                        >
                                          <option value="">Sem categoria</option>
                                          {allCategories.map((c) => (
                                            <option key={c.id} value={c.id}>{c.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small mb-1">Centro de custo</label>
                                        <select
                                          className="form-select form-select-sm"
                                          value={editRuleValues.costCenterId ?? ""}
                                          onChange={(e) => setEditRuleValues((v) => ({ ...v, costCenterId: e.target.value ? Number(e.target.value) : null }))}
                                        >
                                          <option value="">Sem centro de custo</option>
                                          {allCostCenters.map((c) => (
                                            <option key={c.id} value={c.id}>{c.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <AutocompleteInput
                                          label="Conta contábil"
                                          placeholder="Buscar conta"
                                          value={editRuleValues.accountId ?? null}
                                          selectedLabel={editRuleAccountLabel}
                                          fetchOptions={fetchAccounts}
                                          onSelect={(opt) => {
                                            setEditRuleValues((v) => ({ ...v, accountId: opt?.id ?? null }));
                                            setEditRuleAccountLabel(opt?.label);
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div className="mt-2 d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => void updateRule(rule.id, editRuleValues)}
                                        disabled={savingEditRule}
                                      >
                                        {savingEditRule ? "Salvando..." : "Salvar"}
                                      </button>
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => setEditingRule(null)}
                                        disabled={savingEditRule}
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2 */}
          {preview && items.length > 0 && (
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                  <div>
                    <h5 className="card-title mb-1">2. Revisar e importar</h5>
                    <div className="text-muted small">
                      {preview.bankAccount.bankName && <span className="me-2">{preview.bankAccount.bankName}</span>}
                      ag {preview.bankAccount.agency} / cc {preview.bankAccount.accountNumber}
                      {preview.dateStart && (
                        <span className="ms-3">
                          {formatDateBR(preview.dateStart)} — {formatDateBR(preview.dateEnd ?? preview.dateStart)}
                        </span>
                      )}
                      {preview.bankAccount.remunerationAccountLabel && (
                        <span className="ms-3 text-info small">
                          Conta remuneração: {preview.bankAccount.remunerationAccountLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className="badge text-bg-secondary">Total: {preview.stats.total}</span>
                    <span className="badge text-bg-success">Novos: {preview.stats.new}</span>
                    {preview.stats.duplicate > 0 && (
                      <span className="badge text-bg-warning text-dark">Duplicados: {preview.stats.duplicate}</span>
                    )}
                    {preview.stats.transfer > 0 && (
                      <span className="badge text-bg-info text-dark">Transferências: {preview.stats.transfer}</span>
                    )}
                  </div>
                </div>

                {/* Bulk defaults */}
                <div className="border rounded p-3 mb-3 bg-light">
                  <div className="fw-semibold small mb-2">Aplicar em massa nos itens selecionados (Movimentações)</div>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <AutocompleteInput
                        label="Categoria padrão"
                        placeholder="Buscar categoria"
                        value={defaultCategoryOption?.id ?? null}
                        selectedLabel={defaultCategoryOption?.label}
                        fetchOptions={fetchCategories}
                        onSelect={setDefaultCategoryOption}
                      />
                    </div>
                    <div className="col-md-4">
                      <AutocompleteInput
                        label="Centro de custo padrão"
                        placeholder="Buscar centro"
                        value={defaultCostCenterOption?.id ?? null}
                        selectedLabel={defaultCostCenterOption?.label}
                        fetchOptions={fetchCostCenters}
                        onSelect={setDefaultCostCenterOption}
                      />
                    </div>
                    <div className="col-md-4">
                      <AutocompleteInput
                        label="Conta contábil padrão"
                        placeholder="Buscar conta"
                        value={defaultAccountOption?.id ?? null}
                        selectedLabel={defaultAccountOption?.label}
                        fetchOptions={fetchAccounts}
                        onSelect={setDefaultAccountOption}
                      />
                    </div>
                    <div className="col-12">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={applyDefaults}
                        disabled={!defaultCategoryOption && !defaultCostCenterOption && !defaultAccountOption}
                      >
                        Aplicar nos selecionados
                      </button>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-sm align-middle" style={{ fontSize: "0.85rem" }}>
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={items.filter((it) => it.status === "NEW").every((it) => it.selected)}
                            onChange={toggleAll}
                          />
                        </th>
                        <th>Data</th>
                        <th>Memo</th>
                        <th>Descrição</th>
                        <th>Tipo</th>
                        <th>Valor</th>
                        <th>Categoria</th>
                        <th>C. Custo</th>
                        <th>Ct. Contábil</th>
                        <th>C.A Pagar/Receber</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const isTransfer = item.itemType === "TRANSFER";
                        const isSkip = item.itemType === "SKIP";
                        const isDuplicate = item.status === "DUPLICATE";
                        const rowClass = isDuplicate
                          ? "table-light"
                          : isSkip
                          ? "opacity-50"
                          : isTransfer
                          ? "table-info"
                          : item.selected
                          ? ""
                          : "opacity-50";

                        return (
                          <tr key={item.fitId} className={rowClass}>
                            <td>
                              {!isDuplicate && (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={item.selected}
                                  onChange={() => updateItem(idx, { selected: !item.selected })}
                                />
                              )}
                            </td>
                            <td className="text-nowrap">{formatDateBR(item.date)}</td>
                            <td style={{ maxWidth: 160 }}>
                              <span
                                className="text-muted small text-truncate d-block"
                                style={{ maxWidth: 150 }}
                                title={item.memo}
                              >
                                {item.memo}
                              </span>
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <input
                                className="form-control form-control-sm"
                                value={item.description}
                                onChange={(e) => updateItem(idx, { description: e.target.value })}
                                disabled={!item.selected || isDuplicate}
                              />
                            </td>
                            <td>
                              {isTransfer ? (
                                itemTypeBadge("TRANSFER")
                              ) : isSkip ? (
                                itemTypeBadge("SKIP")
                              ) : (
                                <span className={`badge ${item.type === "IN" ? "text-bg-success" : "text-bg-danger"}`}>
                                  {item.type === "IN" ? "Entrada" : "Saída"}
                                </span>
                              )}
                            </td>
                            <td className="text-nowrap fw-semibold">{formatMoneyBR(item.amount)}</td>
                            <td style={{ minWidth: 150 }}>
                              {!isTransfer && !isSkip && !isDuplicate ? (
                                <AutocompleteInput
                                  label=""
                                  placeholder="Categoria"
                                  value={item.categoryId ?? null}
                                  selectedLabel={item.categoryLabel ?? undefined}
                                  fetchOptions={fetchCategories}
                                  onSelect={(opt) =>
                                    updateItem(idx, { categoryId: opt?.id ?? null, categoryLabel: opt?.label ?? null })
                                  }
                                  disabled={!item.selected}
                                />
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td style={{ minWidth: 140 }}>
                              {!isTransfer && !isSkip && !isDuplicate ? (
                                <AutocompleteInput
                                  label=""
                                  placeholder="C. Custo"
                                  value={item.costCenterId ?? null}
                                  selectedLabel={item.costCenterLabel ?? undefined}
                                  fetchOptions={fetchCostCenters}
                                  onSelect={(opt) =>
                                    updateItem(idx, { costCenterId: opt?.id ?? null, costCenterLabel: opt?.label ?? null })
                                  }
                                  disabled={!item.selected}
                                />
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td style={{ minWidth: 150 }}>
                              {!isTransfer && !isSkip && !isDuplicate ? (
                                <AutocompleteInput
                                  label=""
                                  placeholder="Ct. Contábil"
                                  value={item.accountId ?? null}
                                  selectedLabel={item.accountLabel ?? undefined}
                                  fetchOptions={fetchAccounts}
                                  onSelect={(opt) =>
                                    updateItem(idx, { accountId: opt?.id ?? null, accountLabel: opt?.label ?? null })
                                  }
                                  disabled={!item.selected}
                                />
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td style={{ minWidth: 120 }}>
                              {!isTransfer && !isSkip && !isDuplicate && item.selected && (
                                <div className="d-flex flex-column gap-1">
                                  {item.type === "OUT" && (
                                    <div className="form-check form-check-sm mb-0">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`payable-${item.fitId}`}
                                        checked={item.createPayable}
                                        onChange={(e) => updateItem(idx, { createPayable: e.target.checked })}
                                      />
                                      <label className="form-check-label small" htmlFor={`payable-${item.fitId}`}>
                                        C.A Pagar
                                      </label>
                                    </div>
                                  )}
                                  {item.type === "IN" && (
                                    <div className="form-check form-check-sm mb-0">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`receivable-${item.fitId}`}
                                        checked={item.createReceivable}
                                        onChange={(e) => updateItem(idx, { createReceivable: e.target.checked })}
                                      />
                                      <label className="form-check-label small" htmlFor={`receivable-${item.fitId}`}>
                                        C.A Receber
                                      </label>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              {isDuplicate ? (
                                <div className="d-flex flex-column gap-1">
                                  <button
                                    type="button"
                                    className="badge text-bg-warning text-dark border-0 text-start"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => item.existingMovementId && void openDuplicateModal(item.existingMovementId)}
                                    title="Ver movimento existente"
                                  >
                                    Duplicado{item.existingMovementId ? ` #${item.existingMovementId}` : ""}
                                  </button>
                                  {item.reconciled ? (
                                    <span className="badge text-bg-success" style={{ fontSize: "0.65rem" }}>
                                      ✓ Reconciliado
                                    </span>
                                  ) : item.existingMovementId ? (
                                    <button
                                      type="button"
                                      className="badge text-bg-light text-secondary border border-secondary border-opacity-25 text-start"
                                      style={{ cursor: "pointer", fontSize: "0.65rem" }}
                                      disabled={reconcilingId === item.existingMovementId}
                                      onClick={() => void reconcileMovement(item.existingMovementId!)}
                                      title="Confirmar que este movimento já está correto no sistema"
                                    >
                                      {reconcilingId === item.existingMovementId ? "..." : "Reconciliar"}
                                    </button>
                                  ) : null}
                                  {item.existingMovementId && (
                                    <button
                                      type="button"
                                      className="badge bg-white text-danger border border-danger border-opacity-50 text-start"
                                      style={{ cursor: "pointer", fontSize: "0.65rem" }}
                                      onClick={() => dismissDuplicate(item.existingMovementId!)}
                                      title="Este não é um duplicado — criar novo lançamento e reconciliar"
                                    >
                                      ✕ Não é duplicado
                                    </button>
                                  )}
                                </div>
                              ) : item.ruleId ? (
                                <span className="badge text-bg-primary" title={`Regra #${item.ruleId}`}>
                                  Auto
                                </span>
                              ) : item.shouldReconcile ? (
                                <span className="badge text-bg-success" title="Será importado e reconciliado automaticamente">
                                  Novo ✓
                                </span>
                              ) : (
                                <span className="badge text-bg-success">Novo</span>
                              )}
                            </td>
                            <td>
                              {item.selected && !isDuplicate && (
                                <button
                                  className="btn btn-sm btn-outline-secondary text-nowrap"
                                  onClick={() => void saveAsRule(item)}
                                  disabled={savingRule}
                                  title="Salvar padrão do memo como regra automática"
                                >
                                  + Regra
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    {selectedItems.length} selecionado(s) de {items.length}
                  </small>
                  <button
                    className="btn btn-success"
                    onClick={handleCommit}
                    disabled={committing || selectedItems.length === 0}
                  >
                    {committing
                      ? "Importando..."
                      : `Importar ${selectedItems.length} item(ns)`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Duplicate Movement Modal */}
      {duplicateModal && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Movimento existente{" "}
                  <a
                    href={`/financeiro/movimentos?id=${duplicateModal.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary"
                    title="Abrir movimento"
                  >
                    #{duplicateModal.id}
                  </a>
                </h5>
                <button type="button" className="btn-close" onClick={() => setDuplicateModal(null)} />
              </div>
              <div className="modal-body">
                {loadingDuplicate ? (
                  <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
                ) : duplicateModal.data ? (
                  <>
                    <div className="row g-3 mb-3">
                      {(() => {
                        const d = duplicateModal.data;
                        const bankAccount = d.bankAccount as Record<string, unknown> | null;
                        const category = d.category as Record<string, unknown> | null;
                        const costCenter = d.costCenter as Record<string, unknown> | null;
                        const account = d.account as Record<string, unknown> | null;
                        const reconciliation = d.reconciliation as Record<string, unknown> | null;
                        const bankLabel = bankAccount
                          ? `${(bankAccount.bank as Record<string,unknown> | null)?.name ?? ""} ag ${bankAccount.agency} / cc ${bankAccount.accountNumber}`.trim()
                          : "—";
                        const reconcileStatus = reconciliation
                          ? String(reconciliation.status ?? "")
                          : null;
                        return [
                          ["Data", formatDateBR(String(d.occurredAt ?? ""))],
                          ["Descrição", String(d.description ?? "")],
                          ["Valor", formatMoneyBR(Number(d.amount ?? 0))],
                          ["Tipo", d.type === "IN" ? "Entrada" : "Saída"],
                          ["Conta Bancária", bankLabel],
                          ["Origem", String(d.origin ?? "")],
                          ["Categoria", category ? `${category.code ? category.code + " " : ""}${category.name}` : "—"],
                          ["Centro de Custo", costCenter ? `${costCenter.code ? costCenter.code + " " : ""}${costCenter.name}` : "—"],
                          ["Conta Contábil", account ? `${account.code} - ${account.name}` : "—"],
                          ["Conciliação", reconcileStatus
                            ? reconcileStatus === "MATCHED"
                              ? "✓ Conciliado"
                              : reconcileStatus === "REJECTED"
                              ? "✗ Rejeitado"
                              : "Pendente"
                            : "Não conciliado"],
                        ].map(([label, value]) => (
                          <div key={label} className="col-md-6">
                            <div className="text-muted small">{label}</div>
                            <div className="fw-semibold">{value}</div>
                          </div>
                        ));
                      })()}
                    </div>
                    {/* Reconciliation status alert */}
                    {(() => {
                      const reconciliation = duplicateModal.data.reconciliation as Record<string, unknown> | null;
                      const isReconciled = reconciliation && reconciliation.status === "MATCHED";
                      const isReconciling = reconcilingId === duplicateModal.id;
                      const itemReconciled = items.find((it) => it.existingMovementId === duplicateModal.id)?.reconciled;
                      if (isReconciled || itemReconciled) {
                        return (
                          <div className="alert alert-success py-2 mb-0">
                            <strong>✓ Movimento reconciliado</strong> — confirmado que o registro está correto.
                            {reconciliation?.matchedAt && (
                              <span className="ms-2 text-muted small">
                                em {formatDateBR(String(reconciliation.matchedAt))}
                              </span>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div className="alert alert-warning py-2 mb-0 d-flex align-items-center justify-content-between gap-2">
                          <span className="small">
                            Este movimento ainda não foi reconciliado. Confirme que o registro está correto antes de prosseguir.
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-success text-nowrap"
                            disabled={isReconciling}
                            onClick={() => void reconcileMovement(duplicateModal.id)}
                          >
                            {isReconciling ? "Reconciliando..." : "✓ Marcar como reconciliado"}
                          </button>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-muted">Não foi possível carregar o movimento.</div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDuplicateModal(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
